import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import * as fromNotificationsActions from './notifications.actions';
import * as fromLoginSelectors from '../../../../../app/access/login/state/login.selectors';
import { ActivatedRoute } from '@angular/router';
import { User } from '../../../../shared/user/user.model';
import { Notification } from '../notification.model';
import { NotificationsService } from '../notifications.service';
import { AppState } from '../../../../shared/state/app.reducer';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class NotificationsEffects {

  loadNotifications$ = createEffect( () =>
    this.actions$.pipe(
      ofType(
        fromNotificationsActions.loadNotifications,
        fromNotificationsActions.setAsReadSuccess,
      ),
      withLatestFrom(
        this.store.pipe( select( fromLoginSelectors.selectLoginLoggedUser ) ),
      ),
      mergeMap( ([ , user ]: [ any, User ]) => {
        return this.notificationService.getNotifications( user.id );
      } ),
      catchError( (err, caught$) => {
        this.store.dispatch( fromNotificationsActions.loadNotificationsFailure() );
        return caught$;
      } ),
      map( (notifications: Notification[]) => {
        return fromNotificationsActions.loadNotificationsSuccess( { notifications } );
      } ),
    ),
  );

  connectToWebSocket$ = createEffect( () =>
    this.actions$.pipe(
      ofType(
        fromNotificationsActions.connectToNotifications
      ),
      filter( () => !this.notificationService.isConnected() ),
      switchMap( () =>
        this.notificationService.connect().pipe(
          map( (notification) => {
              if ( notification.action === 'ERROR' ) {
                this.toastr.error(
                  notification.description,
                  this.translate.instant( `NOTIFICATIONS.${ notification.action }` )
                );
              } else {
                this.toastr.success(
                  notification.description,
                  notification.action ? this.translate.instant( `NOTIFICATIONS.${ notification.type.toUpperCase() }.${ notification.action }` ):
                    this.translate.instant( `NOTIFICATIONS.${ notification.type.toUpperCase() }.TITLE` )
                );
              }

              const audio = new Audio();
              audio.src = 'assets/sounds/notification.mp3';
              audio.load();
              audio.play().catch( err => console.warn( 'Falha ao tocar o som:', err ) );

              return fromNotificationsActions.notificationReceived( { notification } );
            }
          ),
          catchError( (error, caught$) => {
            console.error( '[WebSocket Error]', error );
            return caught$;
          } )
        )
      )
    )
  );

  disconnectToWebSocket$ = createEffect( () =>
      this.actions$.pipe(
        ofType(
          fromNotificationsActions.disconnectToNotifications
        ),
        map( () => {
          this.notificationService.disconnect();
        } ),
      ),
    { dispatch: false },
  );

  setAsRead$ = createEffect( () =>
    this.actions$.pipe(
      ofType(
        fromNotificationsActions.setAsRead
      ),
      mergeMap( ({ notification }: { notification: Notification }) =>
        this.notificationService.setAsRead( notification ).pipe(
          map( () => {
            return fromNotificationsActions.setAsReadSuccess( { notification } );
          } ),
          catchError( (error, caught$) => {
            return of( fromNotificationsActions.setAsReadFailure );
          } ),
        )
      ),
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private notificationService: NotificationsService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private translate: TranslateService){
  }
}
